import { useState, useEffect } from 'react';
import { Radio, Users, Play, X, Maximize2, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { cn } from '@/lib/utils';
import { GlassSurface } from '@/components/design/GlassSurface';
import { useSocialStore } from '@/store/socialStore';

export function RightSidebar() {
    // Shared state for the video player
    const [activeVideo, setActiveVideo] = useState<EventItem | null>(null);

    return (
        <div className="hidden lg:block w-[320px] pl-8">
            <div className="sticky top-8 space-y-6">
                {/* 1. Live Events / Replay Widget */}
                <LiveEventsWidget onPlay={(video) => setActiveVideo(video)} />

                {/* 2. Ads Widget (Placeholder) */}
                <AdsWidget />

                {/* 3. Suggested Follows */}
                <SuggestedFollows />
            </div>

            {/* Floating Video Player Overlay */}
            <AnimatePresence>
                {activeVideo && (
                    <FloatingVideoPlayer
                        video={activeVideo}
                        onClose={() => setActiveVideo(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// ─── Floating Video Player ────────────────────────────────────────

function FloatingVideoPlayer({ video, onClose }: { video: EventItem; onClose: () => void }) {
    const [isMinimized, setIsMinimized] = useState(false);
    const dragControls = useDragControls();

    return (
        <motion.div
            drag
            dragListener={false}
            dragControls={dragControls}
            dragMomentum={false}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                width: isMinimized ? 320 : 480,
            }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={cn(
                "fixed z-[100] bg-bg-surface border border-white/10 shadow-2xl overflow-hidden backdrop-blur-xl",
                isMinimized ? "bottom-24 right-6 rounded-xl" : "bottom-24 right-6 rounded-2xl"
            )}
        >
            {/* Header / Controls */}
            <div
                onPointerDown={(e) => dragControls.start(e)}
                className="flex items-center justify-between p-3 bg-black/20 border-b border-white/5 handle cursor-move touch-none"
            >
                <div className="flex items-center gap-2 overflow-hidden pointer-events-none">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-bold text-white truncate">{video.title}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="p-1 hover:bg-white/10 rounded-lg text-text-muted hover:text-white transition-colors"
                    >
                        {isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-red-500/20 rounded-lg text-text-muted hover:text-red-500 transition-colors"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* Video Container */}
            <div className={cn("relative bg-black transition-all duration-300", isMinimized ? "hidden" : "aspect-video")}>
                <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0"
                />
            </div>
        </motion.div>
    );
}

// ─── Live Events & Replays Component ──────────────────────────────

interface EventItem {
    id: string;
    title: string;
    subtitle: string;
    link: string;
    thumbnailColor: string; // Add variety
    date?: string;
}

const REPLAYS_2026: EventItem[] = [
    {
        id: '4krcNb8vSNQ',
        title: 'Penn State vs. Ohio State',
        subtitle: 'FULL DUAL | B1G Wrestling',
        link: 'https://www.youtube.com/watch?v=4krcNb8vSNQ',
        thumbnailColor: 'bg-blue-900',
        date: 'Feb 13'
    },
    {
        id: 'qbUcn2hEyE8',
        title: 'Oklahoma vs. Oklahoma St.',
        subtitle: 'BEDLAM DUAL | Full Replay',
        link: 'https://www.youtube.com/watch?v=qbUcn2hEyE8',
        thumbnailColor: 'bg-orange-700',
        date: 'Jan 22'
    },
    {
        id: 'DlxaYePP9YM',
        title: 'Zerban vs. Watters (157)',
        subtitle: 'Big 12 Full Match',
        link: 'https://www.youtube.com/watch?v=DlxaYePP9YM',
        thumbnailColor: 'bg-red-600',
        date: 'Feb 14'
    },
    {
        id: 'QkXiJPgL6dg',
        title: 'Spratley vs. Roach (125)',
        subtitle: 'Big 12 Full Match',
        link: 'https://www.youtube.com/watch?v=QkXiJPgL6dg',
        thumbnailColor: 'bg-red-600',
        date: 'Feb 07'
    },
    {
        id: 'Is8QJCkwqS8',
        title: 'Spratley vs. Anderson (125)',
        subtitle: 'Big 12 Full Match',
        link: 'https://www.youtube.com/watch?v=Is8QJCkwqS8',
        thumbnailColor: 'bg-red-600',
        date: 'Jan 31'
    },
    {
        id: '438MH2FdzCU',
        title: 'Larkin vs. Walker (133)',
        subtitle: 'Big 12 Full Match',
        link: 'https://www.youtube.com/watch?v=438MH2FdzCU',
        thumbnailColor: 'bg-red-600',
        date: 'Jan 15'
    }
];

function LiveEventsWidget({ onPlay }: { onPlay: (video: EventItem) => void }) {
    const [activeTab, setActiveTab] = useState<'live' | 'replay'>('replay');
    const [hasLiveEvents] = useState(false);

    useEffect(() => {
        if (!hasLiveEvents) {
            setActiveTab('replay');
        }
    }, [hasLiveEvents]);

    return (
        <GlassSurface variant="panel" className="p-0 relative overflow-hidden group flex flex-col max-h-[400px]">
            {/* Header Tabs - Sticky */}
            <div className="flex items-center justify-between p-4 pb-2 border-b border-white/5 bg-bg-surface2/50 backdrop-blur-md sticky top-0 z-20">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setActiveTab('live')}
                        className={cn(
                            "text-xs font-black uppercase tracking-wider transition-colors relative pb-1",
                            activeTab === 'live' ? "text-white" : "text-text-muted hover:text-text-primary"
                        )}
                    >
                        Live
                        {activeTab === 'live' && (
                            <motion.div layoutId="tab-underline" className="absolute -bottom-[9px] left-0 right-0 h-0.5 bg-gold-500" />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('replay')}
                        className={cn(
                            "text-xs font-black uppercase tracking-wider transition-colors relative pb-1",
                            activeTab === 'replay' ? "text-white" : "text-text-muted hover:text-text-primary"
                        )}
                    >
                        2026 Season
                        {activeTab === 'replay' && (
                            <motion.div layoutId="tab-underline" className="absolute -bottom-[9px] left-0 right-0 h-0.5 bg-gold-500" />
                        )}
                    </button>
                </div>

                {hasLiveEvents && (
                    <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-500/10 text-[9px] font-black text-red-500 border border-red-500/20 animate-pulse uppercase tracking-wide">
                        <Radio size={10} /> LIVE
                    </span>
                )}
            </div>

            {/* Content Area - Scrollable */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <AnimatePresence mode="wait">
                    {activeTab === 'live' ? (
                        <motion.div
                            key="live"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="flex flex-col items-center justify-center h-[200px] text-center"
                        >
                            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-2">
                                <Radio className="text-text-muted" size={18} />
                            </div>
                            <p className="text-xs font-bold text-text-muted">No live events active.</p>
                            <button
                                onClick={() => setActiveTab('replay')}
                                className="text-[10px] font-bold text-gold-500 mt-2 hover:underline uppercase tracking-wide"
                            >
                                Watch 2026 Replays
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="replay"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-1"
                        >
                            {REPLAYS_2026.map((event, i) => (
                                <motion.div
                                    key={event.id}
                                    onClick={() => onPlay(event)}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="group/item flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-all cursor-pointer border border-transparent hover:border-white/5 relative overflow-hidden"
                                >
                                    {/* Clean Hover Background */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity" />

                                    {/* Thumbnail */}
                                    <div className={cn(
                                        "w-12 h-12 rounded-lg flex items-center justify-center shrink-0 shadow-lg relative overflow-hidden group-hover/item:scale-105 transition-transform",
                                        event.thumbnailColor || 'bg-neutral-800'
                                    )}>
                                        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
                                        <Play size={16} className="text-white relative z-10 fill-white opacity-80 group-hover/item:opacity-100 transition-opacity" />

                                        {/* Featured badge for first couple */}
                                        {i < 2 && (
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gold-500" />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0 flex flex-col gap-0.5 relative z-10">
                                        <div className="text-[13px] font-bold text-white truncate group-hover/item:text-gold-400 transition-colors leading-tight">
                                            {event.title}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-text-muted truncate font-medium">
                                                {event.subtitle}
                                            </span>
                                            {event.date && (
                                                <span className="text-[9px] text-white/20 font-bold uppercase tracking-wider">
                                                    {event.date}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Subtle Arrow instead of generic play, appears on hover */}
                                    <div className="w-5 h-5 flex items-center justify-center opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all">
                                        <Play size={10} className="text-gold-500 fill-gold-500" />
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </GlassSurface>
    );
}

// ─── Ads Component (Placeholder) ──────────────────────────────────

function AdsWidget() {
    return (
        <div className="bg-bg-elevated/20 backdrop-blur-sm rounded-xl border border-white/5 p-4 relative overflow-hidden group cursor-pointer hover:border-white/10 transition-colors">
            {/* Background Image Placeholder */}
            <div className="absolute inset-0 bg-gradient-to-br from-gold-900/10 to-transparent z-0 opacity-50 group-hover:opacity-100 transition-opacity" />

            <div className="relative z-10">
                <div className="text-[10px] font-bold text-text-muted mb-1 uppercase tracking-widest flex justify-between">
                    <span>Sponsored</span>
                    <span className="text-white/20 group-hover:text-white/40 transition-colors">Ad</span>
                </div>
                <div className="w-full aspect-video bg-black/40 rounded-lg mb-2 flex items-center justify-center border border-white/5 group-hover:border-gold/20 transition-colors">
                    <span className="text-xs text-white/20 font-bold uppercase group-hover:text-white/40 transition-colors">Rudis / Nike Ad</span>
                </div>
                <h4 className="text-sm font-bold text-white mb-1 group-hover:text-gold-400 transition-colors">Elite Wrestling Camps</h4>
                <p className="text-xs text-text-secondary">Sign up for summer intensives now. Limited spots.</p>
            </div>
        </div>
    );
}

// ─── Suggested Follows Component ──────────────────────────────────

function SuggestedFollows() {
    const { followingIds, followUser } = useSocialStore();
    const suggestions = [
        { id: '1', name: 'wrestler_1', sub: 'New to wrestling' },
        { id: '2', name: 'wrestler_2', sub: 'New to wrestling' },
        { id: '3', name: 'wrestler_3', sub: 'New to wrestling' },
    ];

    return (
        <div className="bg-transparent pt-2">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-text-secondary">Suggested for you</h3>
                <button className="text-xs font-bold text-white hover:text-gold-400 transition-colors">See All</button>
            </div>

            <div className="space-y-4">
                {suggestions.map(user => (
                    <div key={user.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-bg-surface2 flex items-center justify-center border border-white/5">
                                <Users size={16} className="text-white/20" />
                            </div>
                            <div>
                                <div className="text-sm font-bold text-white hover:underline cursor-pointer">{user.name}</div>
                                <div className="text-xs text-text-muted">{user.sub}</div>
                            </div>
                        </div>
                        <button
                            onClick={() => followUser(user.id)}
                            className="text-xs font-bold text-gold-400 hover:text-white transition-colors"
                        >
                            {followingIds.has(user.id) ? 'Following' : 'Follow'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
